/**
 * /submit/[slug] — Public Submission Page
 *
 * Branded landing page where startups submit their pitch deck to an investor.
 * This page is fully public (no authentication required).
 *
 * Flow:
 * 1. Fetch investor link metadata via GET /process-submission?slug=xxx
 * 2. Show investor branding + form
 * 3. On submit, call POST /process-submission with deck + form data
 * 4. Show success or error state
 */

import { useState, useEffect } from 'react';
import { View, Platform, Image, ScrollView, Text, useWindowDimensions, Pressable } from 'react-native';
import { openExternalUrl } from '@/lib/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Zap, ShieldCheck, Mail } from 'lucide-react-native';
import { SubmissionForm } from '@/components/submission/SubmissionForm';
import { SubmissionSuccess } from '@/components/submission/SubmissionSuccess';
import { SubmissionError, type SubmissionErrorType } from '@/components/submission/SubmissionError';
import { useSubmitDeck, type SubmissionFormData } from '@/lib/submission/use-submit-deck';
import {
  resolveSubmissionLinkState,
  resolveSubmissionLinkHttpError,
  type SubmissionLinkPayload,
} from '@/lib/submission/link-state';
import { reportError } from '@/lib/errors';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { LOGO_WORDMARK_WHITE } from '@/lib/logo';

// ============================================================================
// Types
// ============================================================================

// The payload shape lives with the state resolver in link-state.ts so the two
// cannot drift. The local copy this replaces omitted `investorFirm`, which is
// why the render had to cast it back in by hand.
type LinkData = SubmissionLinkPayload;

type PageState = 'loading' | 'error' | 'form' | 'submitting' | 'success' | 'already-rated';

// ============================================================================
// Main Page
// ============================================================================

export default function SubmitDeckPage() {
  const { slug } = useLocalSearchParams<{ slug: string }>();

  // Link metadata state
  const [linkData, setLinkData] = useState<LinkData | null>(null);
  const [linkLoading, setLinkLoading] = useState<boolean>(true);
  const [linkError, setLinkError] = useState<SubmissionErrorType | null>(null);

  // Submission hook
  const { submit, stage, progress, error: submitError, reset } = useSubmitDeck();

  // Form data to persist for success screen
  const [lastFormData, setLastFormData] = useState<{
    companyName: string;
    contactEmail: string;
  }>({ companyName: '', contactEmail: '' });

  // Phase 116.1 — Option A: duplicate-of-rated redirect target.
  const [claimRedirect, setClaimRedirect] = useState<{
    url?: string;
    message?: string;
  } | null>(null);

  // ============================================================================
  // Fetch Link Metadata
  // ============================================================================

  useEffect(() => {
    async function fetchLink() {
      try {
        const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
        const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

        if (!supabaseUrl || !anonKey) {
          setLinkError('server-error');
          return;
        }

        const response = await fetch(
          `${supabaseUrl}/functions/v1/process-submission?slug=${encodeURIComponent(slug as string)}`,
          {
            method: 'GET',
            headers: { 'apikey': anonKey },
          }
        );

        if (!response.ok) {
          const state = resolveSubmissionLinkHttpError(response.status);
          setLinkData(state.linkData);
          setLinkError(state.error);
          return;
        }

        // A paused link is a KNOWN link — the payload names the investor either
        // way. Resolving identity and availability together (rather than
        // returning early on `!isActive`) is what stops the paused page from
        // addressing the founder as "this investor". See link-state.ts.
        const state = resolveSubmissionLinkState(await response.json());
        setLinkData(state.linkData);
        setLinkError(state.error);
      } catch (err) {
        reportError(err, { context: 'SubmitDeckPage.fetchLink', level: 'warning' });
        setLinkError('server-error');
      } finally {
        setLinkLoading(false);
      }
    }

    if (slug) {
      fetchLink();
    } else {
      setLinkError('not-found');
      setLinkLoading(false);
    }
  }, [slug]);

  // ============================================================================
  // Web Page Title
  // ============================================================================

  useEffect(() => {
    if (Platform.OS === 'web' && linkData?.investorName) {
      document.title = `Submit to ${linkData.investorName} | Inverio`;
    }
  }, [linkData]);

  // ============================================================================
  // Page State Machine
  // ============================================================================

  const pageState: PageState = linkLoading
    ? 'loading'
    : linkError
      ? 'error'
      : stage === 'complete'
        ? 'success'
        : stage === 'already-rated'
          ? 'already-rated'
          : stage !== 'idle' && stage !== 'error'
            ? 'submitting'
            : 'form';

  // ============================================================================
  // Submit Handler
  // ============================================================================

  const handleSubmit = async (data: SubmissionFormData) => {
    setLastFormData({
      companyName: data.companyName,
      contactEmail: data.contactEmail,
    });
    setClaimRedirect(null);
    const result = await submit(data);
    if (result.code === 'already_rated') {
      setClaimRedirect({ url: result.claimUrl, message: result.message });
    }
  };

  // ============================================================================
  // Reset Handler (for "Submit Another")
  // ============================================================================

  const handleSubmitAnother = () => {
    reset();
    setLastFormData({ companyName: '', contactEmail: '' });
    setClaimRedirect(null);
  };

  // ============================================================================
  // Render
  // ============================================================================

  const colors = usePublicThemeColors();
  const isWebPlatform = Platform.OS === 'web';
  const { width: viewportWidth } = useWindowDimensions();
  const router = useRouter();
  const goHome = () => router.push('/');

  // Split below this width = stacked. Mirrors AuthLayout's desktop breakpoint.
  const SPLIT_BREAKPOINT = 1024;
  const isSplit = isWebPlatform && viewportWidth >= SPLIT_BREAKPOINT;

  const investorName = linkData?.investorName?.trim() || 'this investor';
  const investorFirm = linkData?.investorFirm?.trim() || null;
  const firstName = investorName.split(' ')[0];
  const initials = investorName
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
  const roleLine = investorFirm ? `Angel Investor · ${investorFirm}` : 'Angel Investor';

  // -----------------------------------------------------------------
  // LEFT PANEL — dark navy gradient marketing surface (mirrors AuthLayout)
  // -----------------------------------------------------------------

  const heroPanel = (
    <LinearGradient
      colors={['#0A1628', '#132240', '#1A3058']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{
        flex: 1,
        padding: isSplit ? 56 : 28,
        minHeight: isSplit ? undefined : 420,
      }}
    >
      {/* Top: Inverio wordmark (clickable — returns to the inverio.ai homepage).
          Negative marginLeft compensates for the image asset's internal
          transparent padding so the visible "i" of "inverio" aligns with
          the left edge of the text content below. */}
      <View style={{ alignItems: 'flex-start', marginBottom: 32, marginLeft: -10 }}>
        <Pressable
          onPress={goHome}
          accessibilityRole="link"
          accessibilityLabel="Inverio — go to homepage"
        >
          <Image
            source={LOGO_WORDMARK_WHITE}
            style={{ width: 200, height: 50, resizeMode: 'contain' }}
            accessibilityLabel="Inverio"
          />
        </Pressable>
      </View>

      {/* Middle: vertically centered marketing block, aligned to panel inset. */}
      <View style={{ flex: 1, justifyContent: 'center', gap: 28 }}>
        {/* Avatar + name on the same row.
            Avatar uses the teal brand color as the fallback (no profile
            picture yet); when picture support lands it slots in here. */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
          <View
            style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: '#4fd1c5',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text style={{ color: '#0A1628', fontSize: 28, fontWeight: '700' }}>
              {linkLoading ? '·' : initials}
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                color: 'white',
                fontSize: 32,
                fontWeight: '700',
                lineHeight: 36,
                letterSpacing: -0.3,
              }}
              numberOfLines={2}
            >
              {linkLoading ? 'Loading…' : investorName}
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, marginTop: 4 }}>
              {linkLoading ? ' ' : roleLine}
            </Text>
          </View>
        </View>

        {/* Thesis quote — teal left rule with eyebrow label */}
        {linkData?.thesisMessage ? (
          <View
            style={{
              borderLeftWidth: 3,
              borderLeftColor: '#4fd1c5',
              paddingLeft: 18,
            }}
          >
            <Text
              style={{
                color: '#4fd1c5',
                fontSize: 11,
                fontWeight: '700',
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              My thesis
            </Text>
            <Text
              style={{
                color: 'rgba(255,255,255,0.92)',
                fontSize: 17,
                lineHeight: 26,
                fontStyle: 'italic',
              }}
            >
              "{linkData.thesisMessage.trim()}"
            </Text>
          </View>
        ) : !linkLoading ? (
          <Text
            style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: 15,
              lineHeight: 22,
            }}
          >
            Concise pitch decks, candid feedback.
          </Text>
        ) : null}

        {/* Feature rows — same left edge as everything above */}
        <View style={{ gap: 18, marginTop: 8 }}>
          <FeatureRow
            icon={<Zap size={20} color="white" />}
            title="AI-scored on submission"
            description="Inverio evaluates every deck on 15 dimensions before it lands."
          />
          <FeatureRow
            icon={<Mail size={20} color="white" />}
            title="Personal investor review"
            description="No spam funnel. The investor reads what's worth their time."
          />
          <FeatureRow
            icon={<ShieldCheck size={20} color="white" />}
            title="Hosted in the EU"
            description="Your deck stays in the EU. Anonymous until the investor responds."
          />
        </View>
      </View>

      {/* Bottom: trust footer */}
      <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginTop: 32 }}>
        Built in Geneva, Switzerland. Data hosted in the EU.
      </Text>
    </LinearGradient>
  );

  // -----------------------------------------------------------------
  // RIGHT PANEL — form on theme-aware surface
  // -----------------------------------------------------------------

  const formPanel = (
    <View
      style={{
        flex: 1,
        backgroundColor: colors.surface.primary,
        padding: isSplit ? 48 : 24,
        justifyContent: isSplit ? 'center' : 'flex-start',
      }}
    >
      <View style={{ width: '100%', maxWidth: 480, alignSelf: 'center' }}>
        {pageState === 'success' ? (
          <SubmissionSuccess
            companyName={lastFormData.companyName}
            investorName={investorName}
            contactEmail={lastFormData.contactEmail}
            onSubmitAnother={handleSubmitAnother}
          />
        ) : pageState === 'error' ? (
          <SubmissionError errorType={linkError!} slug={slug as string} />
        ) : pageState === 'already-rated' ? (
          <View>
            <Text
              style={{
                color: colors.text.primary,
                fontSize: 24,
                fontWeight: '700',
                marginBottom: 12,
              }}
            >
              This company has already been scored.
            </Text>
            <View
              style={{
                height: 1,
                backgroundColor: colors.border.light,
                marginBottom: 16,
              }}
            />
            <Text
              style={{
                color: colors.text.secondary,
                fontSize: 15,
                lineHeight: 22,
                marginBottom: 24,
              }}
            >
              {claimRedirect?.message ??
                'A submission for this company has already been reviewed. Visit Inverio to claim your company profile and manage future submissions.'}
            </Text>
            <Pressable
              onPress={() => {
                const target = claimRedirect?.url || 'https://inverio.ai';
                openExternalUrl(target, 'SubmitDeckPage.claimRedirect');
              }}
              style={{
                backgroundColor: '#14B8A6',
                paddingVertical: 14,
                paddingHorizontal: 20,
                borderRadius: 10,
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Text style={{ color: '#0A1628', fontWeight: '700', fontSize: 15 }}>
                Claim your profile on Inverio
              </Text>
            </Pressable>
            <Pressable
              onPress={handleSubmitAnother}
              style={{ paddingVertical: 10, alignItems: 'center' }}
            >
              <Text style={{ color: colors.text.tertiary, fontSize: 14 }}>
                Submit a different company instead
              </Text>
            </Pressable>
          </View>
        ) : (
          <>
            <Text
              style={{
                color: colors.text.primary,
                fontSize: 26,
                fontWeight: '700',
                marginBottom: 16,
              }}
            >
              Submit your pitch to {firstName}
            </Text>
            {/* Section divider */}
            <View
              style={{
                height: 1,
                backgroundColor: colors.border.light,
                marginBottom: 16,
              }}
            />
            <Text
              style={{
                color: colors.text.secondary,
                fontSize: 15,
                marginBottom: 24,
                lineHeight: 22,
              }}
            >
              Tell {firstName} a bit about you, attach your deck, and you're done.
              You'll receive an automated score by email and {firstName} will reach out
              if there's a fit.
            </Text>
            <SubmissionForm
              slug={slug as string}
              investorName={investorName}
              onSubmit={handleSubmit}
              isSubmitting={pageState === 'submitting'}
              stage={stage}
              progress={progress}
            />
            {submitError && stage === 'error' && (
              <View style={{ marginTop: 12 }}>
                <SubmissionError
                  errorType="generic"
                  errorMessage={submitError}
                  onRetry={reset}
                />
              </View>
            )}
          </>
        )}
      </View>
    </View>
  );

  // ---------------- Layouts ----------------

  if (isSplit) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.surface.primary }}>
        <View style={{ flex: 1 }}>{heroPanel}</View>
        <View style={{ flex: 1, maxWidth: 640 }}>{formPanel}</View>
      </View>
    );
  }

  // Stacked (mobile / narrow web): hero on top, form below
  return (
    <View style={{ flex: 1, backgroundColor: colors.surface.primary }}>
      {/* headerShown:false standalone screen with no tab bar → must claim BOTH
          top and bottom insets, or the submit/consent row at the end of the
          form renders under the home indicator. (web supplies its own chrome) */}
      <SafeAreaView edges={isWebPlatform ? [] : ['top', 'bottom']} style={{ flex: 1 }}>
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
          {heroPanel}
          {formPanel}
        </ScrollView>
      </SafeAreaView>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Helper: feature row in the dark panel (mirrors AuthLayout)
// ---------------------------------------------------------------------------

function FeatureRow({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <View style={{ flexDirection: 'row', gap: 14, alignItems: 'flex-start' }}>
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          backgroundColor: 'rgba(255,255,255,0.12)',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </View>
      <View style={{ flex: 1 }}>
        <Text style={{ color: 'white', fontSize: 15, fontWeight: '600' }}>{title}</Text>
        <Text
          style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 13,
            marginTop: 2,
            lineHeight: 18,
          }}
        >
          {description}
        </Text>
      </View>
    </View>
  );
}
